import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Box, Heading, Text, VStack, HStack, Flex, Spinner, Button,
  Badge, Divider, Image, Icon, Container, SimpleGrid, Center,
  ScaleFade, useToast, IconButton
} from "@chakra-ui/react";
import {
  IoCalendarOutline, IoLocationOutline, IoBusinessOutline,
  IoArrowBackOutline, IoTimeOutline, IoInformationCircleOutline,
  IoThumbsUp, IoThumbsUpOutline, IoThumbsDown, IoThumbsDownOutline
} from "react-icons/io5";

import ApiResponse from "../../services/ApiResponse";
import { Evento } from "../../services/models/Evento";
import { EventoTipoDetails } from "../../services/models/Tipo";
import UnauthorizedPage from "../UnauthorizedPage";
import ServerErrorPage from "../ServerErrorPage";

const ACCENT_COLOR = "#f27121"; // Naranja de tu imagen
const CARD_BG = "#131c2e";    // Fondo de las tarjetas en la imagen
const PAGE_BG = "#0b1120";    // Fondo general oscuro

const EventoDetailPage = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const toast = useToast();

  const [response, setResponse] = useState(new ApiResponse<Evento>());
  const [tipo, setTipo] = useState<EventoTipoDetails | null>(null);
  const [userReaction, setUserReaction] = useState<"like" | "dislike" | null>(null);
  const [likeCount, setLikeCount] = useState(0);
  const [dislikeCount, setDislikeCount] = useState(0);
  const [loadingReaction, setLoadingReaction] = useState(false);

  const cardStyle = {
    bg: CARD_BG,
    border: "1px solid",
    borderColor: "whiteAlpha.100",
    borderRadius: "xl",
    p: 6,
  };

  const formatearFechaSafe = (fecha?: string | Date) => {
    if (!fecha) return "No definido";
    const d = new Date(fecha);
    return isNaN(d.getTime()) ? "No definido" : d.toLocaleString("es-AR", {
      day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit", hour12: true
    }).replace(",", " -");
  };

  useEffect(() => {
    const fetchEvento = async () => {
      if (!id) return;
      const res = new ApiResponse<Evento>();
      await res.useFetch(`eventos/${id}`, "GET");
      if (res.data) {
        setResponse(res);
        setLikeCount(res.data.contLikes || 0);
        setDislikeCount(res.data.contDislikes || 0);
      }
    };
    fetchEvento();
  }, [id]);

  useEffect(() => {
    const fetchTipo = async () => {
      if (!response.data) return;
      const tipoRes = new ApiResponse<EventoTipoDetails>();
      await tipoRes.useFetch(`evento-tipos/${response.data.id}`, "GET");
      setTipo(tipoRes.data || null);
    };
    fetchTipo();
  }, [response.data]);

  const handleReaction = async (type: "like" | "dislike") => {
    if (!id || loadingReaction) return;
    const prev = { reaction: userReaction, likes: likeCount, dislikes: dislikeCount };

    if (userReaction === type) {
      setUserReaction(null);
      type === "like" ? setLikeCount(l => l - 1) : setDislikeCount(d => d - 1);
    } else {
      if (userReaction === "like") setLikeCount(l => l - 1);
      if (userReaction === "dislike") setDislikeCount(d => d - 1);
      setUserReaction(type);
      type === "like" ? setLikeCount(l => l + 1) : setDislikeCount(d => d + 1);
    }

    setLoadingReaction(true);
    const api = new ApiResponse<{ likes: number; dislikes: number }>();
    await api.useFetch(`eventos/${id}/reaccionar`, "PATCH", { tipo: userReaction === type ? "delete" : type });
    if (api.responseCode !== 200) {
      setUserReaction(prev.reaction);
      setLikeCount(prev.likes);
      setDislikeCount(prev.dislikes);
    }
    setLoadingReaction(false);
  };

  if (response.loading) return <Center h="100vh" bg={PAGE_BG}><Spinner color={ACCENT_COLOR} /></Center>;
  if (!response.data) return <ServerErrorPage />;

  const evento = response.data;

  return (
    <Box bg={PAGE_BG} color="white" minH="100vh" pb={10}>
      {/* Header / Banner */}
      <Box h="250px" position="relative" mb={8}>
        <Image src="https://images.unsplash.com/photo-1501281668745-f7f57925c3b4?q=80&w=2070" w="100%" h="100%" objectFit="cover" opacity={0.4} />
        <Container maxW="container.xl" position="absolute" top="50%" left="50%" transform="translate(-50%, -50%)">
            <IconButton aria-label="back" icon={<IoArrowBackOutline />} variant="ghost" color="white" mb={4} onClick={() => navigate(-1)} />
            <Badge colorScheme="orange" mb={2}>{tipo?.nombre || "EVENTO"}</Badge>
            <Heading size="2xl">{evento.titulo}</Heading>
        </Container>
      </Box>

      <Container maxW="container.xl">
        <SimpleGrid columns={{ base: 1, lg: 3 }} spacing={6}>
          
          {/* Columna Izquierda */}
          <VStack spacing={6} gridColumn={{ lg: "span 2" }} align="stretch">
            {/* Barra Reacciones */}
            <HStack {...cardStyle} justify="space-around" py={4}>
              <Button 
                variant="unstyled" display="flex" alignItems="center"
                onClick={() => handleReaction("like")}
                color={userReaction === "like" ? ACCENT_COLOR : "white"}
              >
                <HStack><Icon as={userReaction === "like" ? IoThumbsUp : IoThumbsUpOutline} boxSize={5} /><Text>Me gusta {likeCount}</Text></HStack>
              </Button>
              <Divider orientation="vertical" h="20px" borderColor="whiteAlpha.300" />
              <Button 
                variant="unstyled" display="flex" alignItems="center"
                onClick={() => handleReaction("dislike")}
                color={userReaction === "dislike" ? "red.400" : "white"}
              >
                <HStack><Icon as={userReaction === "dislike" ? IoThumbsDown : IoThumbsDownOutline} boxSize={5} /><Text>No me gusta {dislikeCount}</Text></HStack>
              </Button>
            </HStack>

            {/* Detalles Evento */}
            <Box {...cardStyle}>
              <HStack mb={4}>
                <Icon as={IoInformationCircleOutline} color={ACCENT_COLOR} boxSize={6} />
                <Heading size="md">Detalles del evento</Heading>
              </HStack>
              <Text color="whiteAlpha.800" fontSize="lg">
                {evento.descripcion || "Damos inicio a las actividades municipales."}
              </Text>
            </Box>

            {/* Lugar */}
            <Box {...cardStyle}>
               <Heading size="sm" mb={4} fontWeight="bold">Lugar del evento</Heading>
               <HStack spacing={3}>
                  <Icon as={IoLocationOutline} color={ACCENT_COLOR} />
                  <Text fontWeight="bold">{evento.ubicacion || "Ubicación no especificada"}</Text>
               </HStack>
            </Box>
          </VStack>

          {/* Columna Derecha - Información Clave */}
          <Box {...cardStyle} h="fit-content">
            <Heading size="sm" mb={6}>Información Clave</Heading>
            <VStack align="stretch" spacing={6}>
              <InfoRow icon={IoCalendarOutline} label="FECHA DE INICIO" value={formatearFechaSafe(evento.inicio)} />
              <InfoRow icon={IoTimeOutline} label="FINALIZACIÓN" value={formatearFechaSafe(evento.fin)} />
              <InfoRow icon={IoBusinessOutline} label="ORGANIZADO POR" value={evento.institucion_nombre || "Municipalidad"} />
              
              <Button 
                bg={ACCENT_COLOR} color="white" size="lg" borderRadius="lg" mt={4}
                _hover={{ bg: "#d65d1a" }}
                onClick={() => window.open(`https://www.google.com/calendar/render?action=TEMPLATE&text=${evento.titulo}`, "_blank")}
              >
                Añadir a mi Calendario
              </Button>
            </VStack>
          </Box>

        </SimpleGrid>
      </Container>
    </Box>
  );
};

const InfoRow = ({ icon, label, value }: { icon: any, label: string, value: string }) => (
  <HStack spacing={4} align="center">
    <Center bg="#1c263d" p={3} borderRadius="lg" border="1px solid" borderColor="whiteAlpha.100">
      <Icon as={icon} color={label.includes("INICIO") ? "orange.400" : label.includes("FIN") ? "orange.400" : "orange.400"} />
    </Center>
    <VStack align="start" spacing={0}>
      <Text fontSize="10px" color="whiteAlpha.500" fontWeight="extrabold" letterSpacing="wider">{label}</Text>
      <Text fontSize="md" fontWeight="bold">{value}</Text>
    </VStack>
  </HStack>
);

export default EventoDetailPage;